import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar } from '@mantine/dates';
import {
    TextInput,
    SegmentedControl,
    ActionIcon,
    Select,
    MultiSelect,
    Tooltip,
    Switch
} from '@mantine/core';
import { localUser } from '@/services/session-service';
import {
    IconCalendarMonth,
    IconList,
    IconSearch,
    IconChevronLeft,
    IconChevronRight,
    IconCalendar,
    IconAlertCircle,
    IconClock,
    IconTicket,
    IconMessage,
    IconCheck,
    IconPencil,
    IconUser
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useAgenda, useTickets, useUsers } from '@/hooks/api-hooks';
import CardModal from '@/components/card-modal';
import { SkeletonList } from '@/components/snippets';
import { formatDayLong, formatPrice, setWebTitle, stripHtml } from '@/lib/format-utils';
import { RESERVATIONS_PATH, TICKETS_PATH } from '@/lib/navigation';
import { Reservation, Ticket, User } from '@turizi/shared';
import SearchInput from '@/components/search-input';

interface AgendaCategoryItem {
    key: string;
    label: string;
    count: number;
    items: Reservation[];
}

type AgendaItemType = 'reservation' | 'ticket';

interface UnifiedAgendaItem {
    id: string;
    numericId: number;
    type: AgendaItemType;
    deadlineDay: string | null;
    deadlineIso: string | null;
    title: string;
    assigneeName: string;
    assigneeInitial: string;
    assigneeUser?: User | null;
    reservation?: Reservation;
    ticket?: Ticket;
}

interface AgendaGroup {
    key: string;
    label: string;
    icon?: React.ReactNode;
    avatarInitial?: string;
    count: number;
    items: UnifiedAgendaItem[];
}

const PRIORITY_DOTS: Record<string, string> = {
    low: 'bg-slate-300',
    medium: 'bg-blue-500',
    high: 'bg-amber-500',
    urgent: 'bg-rose-500',
};

export default function AgendaPage(): JSX.Element {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    setWebTitle(t('agenda'));

    const rawType = searchParams.get('type') || searchParams.get('tab');
    const itemTypeFilter: 'all' | 'reservations' | 'tickets' =
        rawType === 'reservations' || rawType === 'tickets' ? rawType : 'all';

    const setItemTypeFilter = (val: 'all' | 'reservations' | 'tickets') => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (val === 'all') {
                next.delete('type');
                next.delete('tab');
            } else {
                next.set('type', val);
                next.delete('tab');
            }
            return next;
        }, { replace: true });
    };

    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
    const [filterScope, setFilterScope] = useState<'all' | 'overdue' | 'upcoming'>('all');
    const [sortBy, setSortBy] = useState<'date' | 'category' | 'assignee'>('date');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [showCompleted, setShowCompleted] = useState<boolean>(false);

    // Fetch data: by default only open tickets are queried; when showCompleted is toggled, query all tickets
    const { data: agendaResponse, isLoading: isLoadingAgenda } = useAgenda();
    const ticketParams = useMemo(() => {
        return showCompleted ? {} : { status: 'open' };
    }, [showCompleted]);
    const { data: ticketsResponse, isLoading: isLoadingTickets } = useTickets(ticketParams);
    const { data: usersResponse } = useUsers();

    const isLoading = isLoadingAgenda || isLoadingTickets;

    const usersMap = useMemo(() => {
        return (usersResponse?.data || []).reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
        }, {} as Record<number, User>);
    }, [usersResponse]);

    const currentUser = localUser();
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>(() => {
        return currentUser?.id ? [String(currentUser.id)] : [];
    });

    const userOptions = useMemo(() => {
        const list: { value: string; label: string }[] = [];
        const seen = new Set<string>();

        if (currentUser?.id) {
            const myIdStr = String(currentUser.id);
            list.push({
                value: myIdStr,
                label: t('assigned_to_me'),
            });
            seen.add(myIdStr);
        }

        (usersResponse?.data || []).forEach((u) => {
            const uidStr = String(u.id);
            if (!seen.has(uidStr)) {
                seen.add(uidStr);
                list.push({
                    value: uidStr,
                    label: u.name || u.email || uidStr,
                });
            }
        });

        list.push({
            value: 'unassigned',
            label: t('unassigned'),
        });

        return list;
    }, [usersResponse, currentUser, t]);

    const getDueStatus = (dueDay: string | null, isClosed?: boolean) => {
        if (isClosed) {
            return {
                isNoDate: false,
                isOverdue: false,
                isToday: false,
                isUpcoming: false,
                isClosed: true,
                label: t('status_closed'),
                textClass: 'text-slate-500'
            };
        }
        if (!dueDay) {
            return {
                isNoDate: true,
                isOverdue: false,
                isToday: false,
                isUpcoming: false,
                isClosed: false,
                label: t('agenda_group_no_date'),
                textClass: 'text-gray-400'
            };
        }
        const today = dayjs().startOf('day');
        const due = dayjs(dueDay).startOf('day');
        const diffDays = due.diff(today, 'day');

        if (diffDays < 0) {
            return {
                isNoDate: false,
                isOverdue: true,
                isToday: false,
                isUpcoming: false,
                isClosed: false,
                label: t('agenda_days_overdue', { count: Math.abs(diffDays) }),
                textClass: 'text-rose-600'
            };
        }
        if (diffDays === 0) {
            return {
                isNoDate: false,
                isOverdue: false,
                isToday: true,
                isUpcoming: false,
                isClosed: false,
                label: t('agenda_due_today'),
                textClass: 'text-indigo-600'
            };
        }
        return {
            isNoDate: false,
            isOverdue: false,
            isToday: false,
            isUpcoming: true,
            isClosed: false,
            label: t('agenda_due_in_days', { count: diffDays }),
            textClass: 'text-gray-400'
        };
    };

    // Build reservation agenda items (for reservations, creator is creator/assignee)
    const allReservations: UnifiedAgendaItem[] = useMemo(() => {
        const rawCats = (agendaResponse?.data || []) as AgendaCategoryItem[];
        const list: UnifiedAgendaItem[] = [];

        rawCats.forEach((cat) => {
            (cat.items || []).forEach((r: Reservation) => {
                const creator = r.created_by;
                const creatorUser = creator?.id ? usersMap[creator.id] : null;
                const assigneeName = creator?.name || creatorUser?.name || creator?.email || creatorUser?.email || '';
                const assigneeInitial = (assigneeName || '?').charAt(0).toUpperCase();

                list.push({
                    id: `res-${r.id}`,
                    numericId: r.id || 0,
                    type: 'reservation',
                    deadlineDay: r.payment_due_day || null,
                    deadlineIso: r.payment_due_day ? dayjs(r.payment_due_day).toISOString() : null,
                    title: r.client_business?.name || r.pax_groups?.[0]?.name || r.pax_groups?.[0]?.persons?.[0]?.name || `${t('agenda_reservation_label')} #${r.id}`,
                    assigneeName,
                    assigneeInitial,
                    assigneeUser: creatorUser || creator || null,
                    reservation: r,
                });
            });
        });

        return list;
    }, [agendaResponse, usersMap, t]);

    // Build ticket agenda items (by default only open tickets, or include closed if showCompleted is active)
    const allTickets: UnifiedAgendaItem[] = useMemo(() => {
        const rawTickets = (ticketsResponse?.data || []) as Ticket[];
        const list: UnifiedAgendaItem[] = [];

        rawTickets
            .filter((ticket) => showCompleted || (ticket.status !== 'closed'))
            .filter((ticket) => {
                if (selectedUserIds.length === 0) return true;

                if (ticket.assignee_id) {
                    return selectedUserIds.includes(String(ticket.assignee_id));
                }

                if (selectedUserIds.includes('unassigned')) return true;

                if (ticket.creator_id && selectedUserIds.includes(String(ticket.creator_id))) {
                    return true;
                }

                return false;
            })
            .forEach((ticket: Ticket) => {
                const assigneeUser = ticket.assignee_id ? usersMap[ticket.assignee_id] : (ticket.assignee || null);
                const creatorUser = ticket.creator_id ? usersMap[ticket.creator_id] : (ticket.creator || null);
                const assigneeName = assigneeUser?.name || assigneeUser?.email || creatorUser?.name || creatorUser?.email || '';
                const assigneeInitial = (assigneeName || '?').charAt(0).toUpperCase();
                const deadlineDay = ticket.due_datetime ? dayjs(ticket.due_datetime).format('YYYY-MM-DD') : null;

                list.push({
                    id: `ticket-${ticket.id}`,
                    numericId: ticket.id,
                    type: 'ticket',
                    deadlineDay,
                    deadlineIso: ticket.due_datetime ? dayjs(ticket.due_datetime).toISOString() : null,
                    title: ticket.title,
                    assigneeName,
                    assigneeInitial,
                    assigneeUser: assigneeUser || creatorUser || null,
                    ticket,
                });
            });

        return list;
    }, [ticketsResponse, usersMap, showCompleted, selectedUserIds]);

    // Filter items by type, scope, and search
    const filteredItems = useMemo(() => {
        const todayStr = dayjs().format('YYYY-MM-DD');

        let pool: UnifiedAgendaItem[] = [];
        if (itemTypeFilter === 'all') {
            pool = [...allReservations, ...allTickets];
        } else if (itemTypeFilter === 'reservations') {
            pool = allReservations;
        } else {
            pool = allTickets;
        }

        return pool.filter((item) => {
            const dueDay = item.deadlineDay;

            // Scope filter
            if (filterScope === 'overdue') {
                if (!dueDay || dueDay >= todayStr) return false;
            } else if (filterScope === 'upcoming') {
                if (!dueDay || dueDay < todayStr) return false;
            }

            // Search query filter
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim().replace(/^#/, '');
                const idMatch = String(item.numericId).includes(q);
                const titleMatch = item.title.toLowerCase().includes(q);
                const assigneeMatch = item.assigneeName.toLowerCase().includes(q);

                if (item.type === 'reservation' && item.reservation) {
                    const r = item.reservation;
                    const groupNames = (r.pax_groups || []).flatMap(g => [g.name || '', ...(g.persons || []).map(p => p.name || '')]).join(' ');
                    const clientName = (
                        r.client_business?.name ||
                        groupNames
                    ).toLowerCase();
                    const creatorName = (r.created_by?.name || r.created_by?.email || '').toLowerCase();
                    return idMatch || titleMatch || assigneeMatch || clientName.includes(q) || creatorName.includes(q);
                }

                if (item.type === 'ticket' && item.ticket) {
                    const ticket = item.ticket;
                    const content = stripHtml(ticket.content || '').toLowerCase();
                    const creator = ticket.creator_id ? usersMap[ticket.creator_id] : null;
                    const creatorName = (creator?.name || creator?.email || '').toLowerCase();
                    return idMatch || titleMatch || assigneeMatch || content.includes(q) || creatorName.includes(q);
                }

                return idMatch || titleMatch || assigneeMatch;
            }

            return true;
        });
    }, [allReservations, allTickets, itemTypeFilter, filterScope, searchQuery, usersMap]);

    // Group & sort items according to sortBy ('date' | 'category' | 'assignee')
    const displayGroups: AgendaGroup[] = useMemo(() => {
        const todayStr = dayjs().format('YYYY-MM-DD');

        const sortItemsChronologically = (items: UnifiedAgendaItem[]) => {
            return [...items].sort((a, b) => {
                if (!a.deadlineDay && !b.deadlineDay) return 0;
                if (!a.deadlineDay) return 1;
                if (!b.deadlineDay) return -1;
                return a.deadlineDay.localeCompare(b.deadlineDay);
            });
        };

        if (sortBy === 'category') {
            const reservationItems = sortItemsChronologically(
                filteredItems.filter((item) => item.type === 'reservation')
            );
            const ticketItems = sortItemsChronologically(
                filteredItems.filter((item) => item.type === 'ticket')
            );

            const groups: AgendaGroup[] = [];

            if (reservationItems.length > 0) {
                groups.push({
                    key: 'reservations',
                    label: t('agenda_category_unpaid_reservations'),
                    icon: <IconCalendar size={16} className="text-indigo-600" />,
                    count: reservationItems.length,
                    items: reservationItems,
                });
            }

            if (ticketItems.length > 0) {
                groups.push({
                    key: 'tickets',
                    label: t('agenda_category_tickets'),
                    icon: <IconTicket size={16} className="text-amber-600" />,
                    count: ticketItems.length,
                    items: ticketItems,
                });
            }

            return groups;
        }

        if (sortBy === 'assignee') {
            const map: Record<string, UnifiedAgendaItem[]> = {};

            filteredItems.forEach((item) => {
                const key = item.assigneeName.trim() || t('unassigned');
                if (!map[key]) {
                    map[key] = [];
                }
                map[key].push(item);
            });

            const unassignedLabel = t('unassigned');
            const sortedKeys = Object.keys(map).sort((a, b) => {
                if (a === unassignedLabel) return 1;
                if (b === unassignedLabel) return -1;
                return a.localeCompare(b);
            });

            return sortedKeys.map((key) => {
                const items = sortItemsChronologically(map[key]);
                const isUnassigned = key === unassignedLabel;
                return {
                    key,
                    label: key,
                    avatarInitial: isUnassigned ? '?' : key.charAt(0).toUpperCase(),
                    count: items.length,
                    items,
                };
            });
        }

        // Default: sortBy === 'date' (group into urgency periods: Overdue, Due Today, Upcoming, No Deadline, Completed)
        const overdueItems: UnifiedAgendaItem[] = [];
        const todayItems: UnifiedAgendaItem[] = [];
        const upcomingItems: UnifiedAgendaItem[] = [];
        const noDateItems: UnifiedAgendaItem[] = [];
        const completedItems: UnifiedAgendaItem[] = [];

        filteredItems.forEach((item) => {
            const isClosed = item.type === 'ticket' && (item.ticket?.status === 'closed');
            if (isClosed) {
                completedItems.push(item);
            } else if (!item.deadlineDay) {
                noDateItems.push(item);
            } else if (item.deadlineDay < todayStr) {
                overdueItems.push(item);
            } else if (item.deadlineDay === todayStr) {
                todayItems.push(item);
            } else {
                upcomingItems.push(item);
            }
        });

        const groups: AgendaGroup[] = [];

        if (overdueItems.length > 0) {
            groups.push({
                key: 'overdue',
                label: t('agenda_group_overdue'),
                icon: <IconAlertCircle size={16} className="text-rose-600" />,
                count: overdueItems.length,
                items: sortItemsChronologically(overdueItems),
            });
        }

        if (todayItems.length > 0) {
            groups.push({
                key: 'today',
                label: t('agenda_group_today'),
                icon: <IconClock size={16} className="text-indigo-600" />,
                count: todayItems.length,
                items: sortItemsChronologically(todayItems),
            });
        }

        if (upcomingItems.length > 0) {
            groups.push({
                key: 'upcoming',
                label: t('agenda_group_upcoming'),
                icon: <IconCalendar size={16} className="text-slate-600" />,
                count: upcomingItems.length,
                items: sortItemsChronologically(upcomingItems),
            });
        }

        if (completedItems.length > 0) {
            groups.push({
                key: 'completed',
                label: t('status_closed'),
                icon: <IconCheck size={16} className="text-emerald-600" />,
                count: completedItems.length,
                items: sortItemsChronologically(completedItems),
            });
        }

        if (noDateItems.length > 0) {
            groups.push({
                key: 'no_date',
                label: t('agenda_group_no_date'),
                icon: <IconClock size={16} className="text-gray-400" />,
                count: noDateItems.length,
                items: noDateItems,
            });
        }

        return groups;
    }, [filteredItems, sortBy, t]);

    // Group items by day for calendar view
    const itemsByDay = useMemo(() => {
        const map: Record<string, UnifiedAgendaItem[]> = {};
        filteredItems.forEach((item) => {
            const day = item.deadlineDay;
            if (day) {
                if (!map[day]) map[day] = [];
                map[day].push(item);
            }
        });
        return map;
    }, [filteredItems]);

    const selectedDateItems = selectedDate ? (itemsByDay[selectedDate] || []) : [];

    const renderAgendaItem = (item: UnifiedAgendaItem) => {
        const isReservation = item.type === 'reservation';
        const r = item.reservation;
        const ticket = item.ticket;
        const isClosed = !isReservation && ticket?.status === 'closed';
        const isInProgress = !isReservation && ticket?.status === 'in_progress';
        const dueDay = item.deadlineDay;
        const dueStatus = getDueStatus(dueDay, isClosed);
        const isToday = dueStatus.isToday;
        const isOverdue = dueStatus.isOverdue;

        const commentsCount = ticket?.comments?.length || 0;
        const owedEntries = Object.entries(r?.payment_owed_per_currency || {}).filter(
            ([_, amt]) => Number(amt) > 0
        );

        const titleText = isReservation
            ? (r?.client_business?.name || r?.pax_groups?.[0]?.name || r?.pax_groups?.[0]?.persons?.[0]?.name || `${t('agenda_reservation_label')} #${r?.id}`)
            : (ticket?.title || '');

        const subtitleText = isReservation
            ? `${t('agenda_due_payment')}${r?.start_day && r?.end_day ? ` • ${formatDayLong(r.start_day)} → ${formatDayLong(r.end_day)}` : ''}`
            : (ticket?.content ? stripHtml(ticket.content) : '');

        return (
            <div
                key={item.id}
                onClick={() => navigate(isReservation ? `${RESERVATIONS_PATH}/${item.numericId}` : `${TICKETS_PATH}/${item.numericId}`)}
                className={`
                    ${!isReservation && ticket?.status === 'in_progress' ? 'bg-green-100' : 'bg-white'}
                    ${!isReservation && ticket?.status === 'in_progress' ? 'border border-green-200' : 'border border-slate-200/80'}
                    hover:border-slate-300 hover:bg-slate-50/50 
                    rounded-lg p-2.5 sm:p-3 
                    flex items-center justify-between gap-3 sm:gap-4 
                    transition-all cursor-pointer 
                    shadow-2xs 
                    ${isClosed ? 'opacity-60 bg-slate-50/50' : ''
                    }`}
            >
                {/* Left: Date + Content */}
                <div className="flex items-center gap-3 sm:gap-3.5 min-w-0 flex-1">
                    {/* Minimal Date block */}
                    <div className="w-20 text-center shrink-0 flex flex-row items-baseline gap-1 justify-center">
                        <span className={`text-[10px] font-bold uppercase tracking-wider leading-none ${isClosed
                            ? 'text-slate-400'
                            : isOverdue
                                ? 'text-rose-600'
                                : isToday
                                    ? 'text-indigo-600'
                                    : 'text-slate-400'
                            }`}>
                            {dueDay ? dayjs(dueDay).locale(i18n.language).format('MMM') : '—'}
                        </span>
                        <span className={`text-base font-bold leading-tight mt-0.5 ${isClosed
                            ? 'text-slate-400'
                            : isOverdue
                                ? 'text-rose-700'
                                : isToday
                                    ? 'text-indigo-700'
                                    : dueDay
                                        ? 'text-slate-800'
                                        : 'text-slate-300'
                            }`}>
                            {dueDay ? dayjs(dueDay).format('DD') : '—'}
                        </span>
                        <span className="text-xs text-slate-400 shrink-0">
                            {dueDay ? dayjs(dueDay).format('YYYY') : '—'}
                        </span>
                    </div>

                    {/* Content Column */}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            {isReservation ? <IconCalendar size={14} /> : <IconUser size={14} />}
                            <span className={`text-sm font-semibold truncate ${isClosed ? 'line-through text-slate-400' : 'text-slate-900'
                                }`}>
                                {titleText}
                            </span>
                            <span className="text-xs text-slate-400 shrink-0">
                                {
                                    isReservation
                                        ? `${t('agenda_reservation_label')} #${item.numericId}`
                                        : `${t('agenda_ticket_label')} #${item.numericId} ${ticket?.status ? `[${t(`status_${ticket.status}`)}]` : ''}`
                                }
                            </span>

                        </div>

                        {subtitleText && (
                            <div className="text-xs flex gap-2 items-end">
                                {subtitleText}
                                {
                                    owedEntries.length > 0 && (
                                        <>
                                            {owedEntries.map(([curr, amt]) => (
                                                <span key={curr} className="font-bold">
                                                    {formatPrice(Number(amt), { currency: curr })}
                                                </span>
                                            ))}
                                        </>
                                    )
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Comments, Assignee, Amount / Status */}
                <div className="flex items-center gap-3 shrink-0">
                    {commentsCount > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-slate-400" title={`${commentsCount} ${t('comments')}`}>
                            <IconMessage size={13} />
                            <span>{commentsCount}</span>
                        </div>
                    )}

                    {item.assigneeName ? (
                        <div className="flex items-center gap-1.5" title={item.assigneeName}>
                            <span className="solid-box-border-tinted text-xxs font-medium">
                                {item.assigneeName}
                            </span>
                        </div>
                    ) : (
                        <span className="text-xs text-slate-400 italic">
                            {t('unassigned')}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const reservationsCount = allReservations.length;
    const ticketsCount = allTickets.length;
    const totalCount = reservationsCount + ticketsCount;

    return (
        <div className="panel-content px-3 py-3 sm:px-6 max-w-6xl mx-auto text-gray-900">
            {/* Top Row: Title + Scope Tabs (Left) & Calendar Nav + View Switcher (Right) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                    <SegmentedControl
                        size="xs"
                        value={itemTypeFilter}
                        onChange={(val) => setItemTypeFilter(val as 'all' | 'reservations' | 'tickets')}
                        data={[
                            { value: 'all', label: `${t('agenda_filter_all')} (${totalCount})` },
                            { value: 'reservations', label: `${t('agenda_filter_reservations')} (${reservationsCount})` },
                            { value: 'tickets', label: `${t('agenda_filter_tickets')} (${ticketsCount})` },
                        ]}
                        radius="md"
                    />
                    <Switch
                        size="xs"
                        checked={showCompleted}
                        onChange={(e) => setShowCompleted(e.currentTarget.checked)}
                        label={t('agenda_show_completed')}
                        color="indigo"
                        styles={{
                            label: { fontSize: '11px', fontWeight: 600, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }
                        }}
                    />
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                    {viewMode === 'calendar' && (
                        <div className="flex items-center gap-0.5 bg-gray-50 border border-gray-200 rounded-md p-0.5">
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={() => setCurrentDate(dayjs(currentDate).subtract(1, 'month').toDate())}
                            >
                                <IconChevronLeft size={13} />
                            </ActionIcon>
                            <button
                                type="button"
                                onClick={() => setCurrentDate(new Date())}
                                className="px-1.5 py-0.5 text-[11px] font-semibold text-gray-700 hover:text-indigo-600 transition-colors"
                            >
                                {dayjs(currentDate).format('MMM YYYY')}
                            </button>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={() => setCurrentDate(dayjs(currentDate).add(1, 'month').toDate())}
                            >
                                <IconChevronRight size={13} />
                            </ActionIcon>
                        </div>
                    )}

                    <SegmentedControl
                        size="xs"
                        value={viewMode}
                        onChange={(val) => setViewMode(val as 'list' | 'calendar')}
                        data={[
                            {
                                value: 'list',
                                label: (
                                    <div className="flex items-center gap-1 px-1">
                                        <IconList size={14} />
                                        <span>{t('agenda_view_list')}</span>
                                    </div>
                                )
                            },
                            {
                                value: 'calendar',
                                label: (
                                    <div className="flex items-center gap-1 px-1">
                                        <IconCalendarMonth size={14} />
                                        <span>{t('agenda_view_calendar')}</span>
                                    </div>
                                )
                            }
                        ]}
                        radius="md"
                    />
                </div>
            </div>

            {/* Filter Toolbar: Fixed heights and layout to prevent any jitter */}
            <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-2.5 mb-2 flex flex-col md:flex-row md:items-center justify-between gap-2.5">
                <div className="flex items-center gap-2 flex-wrap flex-1">

                    {/* User / Assignee filter for tickets */}
                    <div className={`transition-opacity ${itemTypeFilter === 'reservations' ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                        <MultiSelect
                            size="xs"
                            placeholder={t('assignee')}
                            data={userOptions}
                            value={selectedUserIds}
                            onChange={setSelectedUserIds}
                            clearable
                            searchable
                            nothingFoundMessage={t('no_results')}
                            radius="md"
                            className="w-auto min-w-[210px] sm:min-w-[250px] [&_.mantine-Pill-label]:max-w-none [&_.mantine-Pill-label]:overflow-visible [&_.mantine-Pill-root]:max-w-none"
                            styles={{
                                input: {
                                    minHeight: '30px',
                                    height: '30px',
                                    overflowX: 'auto',
                                    flexWrap: 'nowrap',
                                    scrollbarWidth: 'none',
                                    whiteSpace: 'nowrap',
                                },
                                pill: {
                                    height: '20px',
                                    fontSize: '11px',
                                    paddingLeft: '6px',
                                    paddingRight: '4px',
                                    maxWidth: 'none',
                                    flexShrink: 0,
                                }
                            }}
                        />
                    </div>

                    {/* Filter scope: All / Overdue / Upcoming */}
                    <Select
                        size="xs"
                        value={filterScope}
                        onChange={(val) => setFilterScope((val as any) || 'all')}
                        data={[
                            { value: 'all', label: t('agenda_all_dues') },
                            { value: 'overdue', label: t('agenda_overdue_only') },
                            { value: 'upcoming', label: t('agenda_upcoming_only') }
                        ]}
                        allowDeselect={false}
                        radius="md"
                        className="w-auto min-w-[195px] sm:min-w-[210px]"
                        styles={{
                            input: {
                                whiteSpace: 'nowrap',
                                textOverflow: 'clip',
                            }
                        }}
                    />

                    {/* Sort by: Date / Category / Assignee */}
                    <Select
                        size="xs"
                        value={sortBy}
                        onChange={(val) => setSortBy((val as 'date' | 'category' | 'assignee') || 'date')}
                        data={[
                            { value: 'date', label: `${t('sort_by')}: ${t('agenda_sort_date')}` },
                            { value: 'category', label: `${t('sort_by')}: ${t('agenda_sort_category')}` },
                            { value: 'assignee', label: `${t('sort_by')}: ${t('agenda_sort_assignee')}` },
                        ]}
                        allowDeselect={false}
                        radius="md"
                        className="w-auto min-w-[200px] sm:min-w-[215px]"
                        styles={{
                            input: {
                                whiteSpace: 'nowrap',
                                textOverflow: 'clip',
                            }
                        }}
                    />
                </div>
            </div>

            {/* search */}
            <SearchInput
                initialValue={searchQuery}
                onSearch={setSearchQuery}
                className='mb-5'
            />

            {/* Loading state */}
            {isLoading && <SkeletonList />}

            {/* View Mode 1: Clean Agenda List */}
            {!isLoading && viewMode === 'list' && (
                <div className="space-y-6">
                    {displayGroups.map((group) => (
                        <div key={group.key}>
                            <div className="flex items-center justify-between pb-2 mb-2">
                                <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2 m-0">
                                    {group.avatarInitial && (
                                        <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold">
                                            {group.avatarInitial}
                                        </div>
                                    )}
                                    {group.icon}
                                    <span>{group.label}</span>
                                    <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                        {group.count}
                                    </span>
                                </h2>
                            </div>

                            <div className="space-y-2">
                                {group.items.map((item) => renderAgendaItem(item))}
                            </div>
                        </div>
                    ))}

                    {displayGroups.length === 0 && (
                        <div className="py-12 text-center text-gray-400 text-xs font-medium bg-white rounded-xl border border-gray-200/80">
                            {t('agenda_no_items')}
                        </div>
                    )}
                </div>
            )}

            {/* View Mode 2: Calendar Grid */}
            {!isLoading && viewMode === 'calendar' && (
                <div className="border border-gray-200 rounded-xl bg-white p-3 shadow-xs">
                    <Calendar
                        locale={i18n.language}
                        date={currentDate}
                        onDateChange={(date) => {
                            if (date) {
                                setCurrentDate(dayjs(date).toDate());
                            }
                        }}
                        styles={{
                            calendarHeader: { maxWidth: '100%', marginBottom: 12 },
                            calendarHeaderLevel: { color: '#0f172a', fontWeight: 'bold', fontSize: '13px' },
                            month: { width: '100%', tableLayout: 'fixed' },
                            day: {
                                height: 56,
                                width: '100%',
                                verticalAlign: 'top',
                                border: '1px solid #f1f5f9',
                                color: '#1e293b',
                                padding: 0
                            },
                            weekday: {
                                color: '#94a3b8',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                paddingBottom: '8px'
                            }
                        }}
                        renderDay={(date) => {
                            const d = dayjs(date);
                            const dateStr = d.format('YYYY-MM-DD');
                            const dayItems = itemsByDay[dateStr] || [];
                            const isToday = d.isSame(dayjs(), 'day');
                            const isPast = d.isBefore(dayjs(), 'day');
                            const count = dayItems.length;

                            return (
                                <div
                                    className={`flex items-start justify-between h-full w-full p-2 cursor-pointer transition-colors ${isToday
                                        ? 'bg-indigo-50/30'
                                        : count > 0
                                            ? 'hover:bg-slate-50'
                                            : 'hover:bg-gray-50/50'
                                        }`}
                                    onClick={() => {
                                        if (count > 0) {
                                            setSelectedDate(dateStr);
                                        }
                                    }}
                                >
                                    <span
                                        className={`text-xs font-semibold ${isToday
                                            ? 'w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-[10px]'
                                            : 'text-slate-500'
                                            }`}
                                    >
                                        {d.date()}
                                    </span>

                                    {count > 0 && (
                                        <span
                                            className={`min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center text-[10px] font-bold ${isPast
                                                ? 'bg-rose-100 text-rose-700'
                                                : isToday
                                                    ? 'bg-indigo-100 text-indigo-700'
                                                    : 'bg-slate-100 text-slate-700'
                                                }`}
                                        >
                                            {count}
                                        </span>
                                    )}
                                </div>
                            );
                        }}
                    />
                </div>
            )}

            {/* Day Detail Modal for Calendar Drilldown */}
            <CardModal
                opened={!!selectedDate}
                header={selectedDate ? t('agenda_day_items_title', { date: formatDayLong(selectedDate) }) : ''}
                onClose={() => setSelectedDate(null)}
                size="lg"
            >
                <div className="space-y-2">
                    {selectedDateItems.map((item) => renderAgendaItem(item))}
                </div>
            </CardModal>
        </div>
    );
}
